import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks/useStores';
import Popup from 'ati-ui-react/components/Popup';
import Link from 'ati-ui-react/components/Link';
import nbspier from 'ati-utils/nbspier';
import { addMetric } from 'utils/addMetrics';
import Button from 'ati-ui-react/components/Button';
import cn from 'classnames';
import getEnvironDomain from 'utils/getEnvironDomain';

import truckIcon from 'src/icons/truck.svg';
import handcuffsIcon from 'src/icons/handcuffs.svg';
import clockIcon from 'src/icons/clock.svg';
import accidentSignIcon from 'src/icons/accident-sign.svg';

import { Wrapper, ConfirmationPopupStyles, Block } from './ConfirmationPopupStyles';

const ConfirmationPopup = observer(({ close, className = '' }) => {
  const {
    checksStore: { buyFullReport, buyingFullReportStatus, priceReport },
    advertsStore: { currentAdvert },
    profileStore: { user },
  } = useStores();

  useEffect(() => {
    addMetric('checker-pay-popup');
  }, []);

  const isEnoughMoney = user.profile.balance >= priceReport;
  const isForbidVirtualAccountOperations = user.profile.contact.forbid_virtual_account_operations;

  const btnDescriptionClassName = cn('btn-description', !isEnoughMoney && 'first');
  const firstLineClassName = cn(
    'first-line',
    (!isEnoughMoney || isForbidVirtualAccountOperations) && 'red',
  );
  const secondLineClassName = cn('second-line', isEnoughMoney && 'gray small');

  const onHandleClickActionBtn = (e) => {
    if (isEnoughMoney) {
      e.preventDefault();
      buyFullReport(currentAdvert?._id, 'checks');
      if (buyingFullReportStatus.isSuccess) {
        close();
      }
    }
  };

  const operationsDescription = () => {
    if (isForbidVirtualAccountOperations) {
      return 'Нет доступа для работы с виртуальным счетом.';
    }

    if (!isEnoughMoney) {
      return 'На вашем счете не хватает средств.';
    }

    return `${priceReport} ат. к оплате`;
  };

  const operationSubDescription = () => {
    if (isForbidVirtualAccountOperations) {
      return (
        <Link
          href={`https://help.${getEnvironDomain()}/personal-account`}
          type="external"
          rel="noopener noreferrer"
          target="_blank"
          small
        >
          Подробнее про виртуальные счета контактов
        </Link>
      );
    }

    if (!isEnoughMoney) {
      return `Для оплаты проверки пополните счет на ${priceReport} атисов.`;
    }

    return 'спишутся с вашего счёта в АТИ';
  };

  const footer = (
    <div className="popup-footer">
      <Button
        onClick={onHandleClickActionBtn}
        className={cn('action-btn', !isEnoughMoney && 'second')}
        isLoading={buyingFullReportStatus.isRequest}
        disabled={buyingFullReportStatus.isRequest || isForbidVirtualAccountOperations}
        href={!isEnoughMoney && `https://billing.${getEnvironDomain()}/addinvoice?option=4`}
        target="_blank"
      >
        {isEnoughMoney ? 'Купить полный отчет' : 'Пополнить счет'}
      </Button>
      <div className={btnDescriptionClassName}>
        <div className={firstLineClassName}>{operationsDescription()}</div>
        <div className={secondLineClassName}>{operationSubDescription()}</div>
      </div>
    </div>
  );

  return (
    <Wrapper>
      <Popup
        title="Отчет о&nbsp;проверке по&nbsp;VIN"
        className={className}
        onClose={close}
        closeOnOverlayClick
        whiteActionArea
        footer={footer}
      >
        <ConfirmationPopupStyles>
          <div className="description">
            Покупайте автомобили без сюрпризов. Отчет&nbsp;содержит:
          </div>
          <Block>
            <img src={truckIcon} alt="Truckmarket" className="icon" width={24} height={24} />
            <div className="content">
              <div className="header">Общие сведения</div>
              <div className="text">
                {nbspier(
                  'Технические характеристики авто из ПТС, включая марку, модель, год выпуска.',
                )}
              </div>
            </div>
          </Block>
          <Block>
            <img src={handcuffsIcon} alt="Truckmarket" className="icon" width={24} height={24} />
            <div className="content">
              <div className="header">Сведения об ограничениях, арестах и залогах</div>
              <div className="text">
                {nbspier(
                  'Если есть залоги на автомобиль, он числится в угоне или имеет другие ограничения на регистрационные действия.',
                )}
              </div>
            </div>
          </Block>
          <Block>
            <img src={clockIcon} alt="Truckmarket" className="icon" width={24} height={24} />
            <div className="content">
              <div className="header">Историю владения</div>
              <div className="text">
                {nbspier('Количество владельцев авто, тип владельца и периоды эксплуатации.')}
              </div>
            </div>
          </Block>
          <Block>
            <img src={accidentSignIcon} alt="Truckmarket" className="icon" width={24} height={24} />
            <div className="content">
              <div className="header">Сведения о ДТП</div>
              <div className="text">{nbspier('История ДТП, в которых участвовал автомобиль.')}</div>
            </div>
          </Block>
        </ConfirmationPopupStyles>
      </Popup>
    </Wrapper>
  );
});

export default ConfirmationPopup;
